import { Download, Maximize2, Minimize2, Scan, ZoomIn, ZoomOut } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

const PdfPageRenderer = lazy(async () => {
  const { Document, Page, pdfjs } = await import('react-pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
  return {
    default: ({
      file,
      pages,
      width,
      onPages,
    }: {
      file: Blob;
      pages: number;
      width: number;
      onPages: (pages: number) => void;
    }) => (
      <Document
        file={file}
        loading={
          <div className="ui-pdf-viewer__state" role="status">
            Preparando páginas…
          </div>
        }
        error={
          <div className="ui-pdf-viewer__state ui-pdf-viewer__state--error" role="alert">
            No se pudo representar el PDF.
          </div>
        }
        onLoadSuccess={({ numPages }) => onPages(numPages)}
      >
        {Array.from({ length: pages }, (_, index) => (
          <Page
            key={index + 1}
            pageNumber={index + 1}
            width={width}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        ))}
      </Document>
    ),
  };
});

export function CanonicalPdfViewer({
  file,
  fileName,
  loading = false,
  error,
  onDownload,
}: {
  file: Blob | null;
  fileName: string;
  loading?: boolean;
  error?: string | undefined;
  onDownload?: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [stageWidth, setStageWidth] = useState(720);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    setPages(0);
    setZoom(1);
  }, [file]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const update = () => setStageWidth(Math.max(260, stage.clientWidth - 32));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(stage);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const toggleFullscreen = async () => {
    if (!rootRef.current) return;
    if (document.fullscreenElement) await document.exitFullscreen();
    else await rootRef.current.requestFullscreen();
  };

  return (
    <section className="ui-pdf-viewer" ref={rootRef} aria-label={`Visor de ${fileName}`}>
      <header className="ui-pdf-viewer__toolbar">
        <strong title={fileName}>{fileName}</strong>
        <div className="ui-pdf-viewer__controls" aria-label="Controles del documento">
          <span aria-live="polite">
            {pages ? `${pages} ${pages === 1 ? 'página' : 'páginas'}` : 'Cargando'}
          </span>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.max(0.6, value - 0.15))}
            disabled={zoom <= 0.6}
            aria-label="Alejar"
            title="Alejar"
          >
            <ZoomOut />
          </button>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.min(2, value + 0.15))}
            disabled={zoom >= 2}
            aria-label="Acercar"
            title="Acercar"
          >
            <ZoomIn />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            aria-label="Ajustar al ancho"
            title="Ajustar al ancho"
          >
            <Scan />
          </button>
          <button
            type="button"
            onClick={() => void toggleFullscreen()}
            aria-label={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            title={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {fullscreen ? <Minimize2 /> : <Maximize2 />}
          </button>
          {onDownload ? (
            <button
              type="button"
              onClick={onDownload}
              aria-label="Descargar PDF"
              title="Descargar PDF"
            >
              <Download />
            </button>
          ) : null}
        </div>
      </header>
      <div className="ui-pdf-viewer__stage" ref={stageRef} tabIndex={0}>
        {loading ? (
          <div className="ui-pdf-viewer__state" role="status">
            Cargando documento…
          </div>
        ) : null}
        {!loading && error ? (
          <div className="ui-pdf-viewer__state ui-pdf-viewer__state--error" role="alert">
            {error}
          </div>
        ) : null}
        {!loading && !error && file ? (
          <Suspense
            fallback={
              <div className="ui-pdf-viewer__state" role="status">
                Preparando visor…
              </div>
            }
          >
            <PdfPageRenderer
              file={file}
              pages={pages}
              width={stageWidth * zoom}
              onPages={setPages}
            />
          </Suspense>
        ) : null}
      </div>
    </section>
  );
}
